import {ApiError, ContentType, HttpMethod, HttpService} from "./HttpService";
import {
    autoSignIn,
    fetchAuthSession,
    signIn,
    SignInOutput,
    signInWithRedirect,
    signOut,
    signUp,
    SignUpOutput,
} from "aws-amplify/auth";
import {InitRegistrationRequest, LoginRequest} from "../model/auth/InitRegistrationRequest";
import {ActivateAccount} from "../model/auth/ActivateAccount";
import {UserToActivate} from "../model/auth/UserToActivate";
import {PublicUser} from "../model/user/User";

type RegistrationSuccessResponse = {
    statusCode: number;
    message: string;
}

type ChangePasswordRequest = {
    newPassword: string;
    resetPasswordCode: string;
}

type RegistrationResponse = RegistrationSuccessResponse | ApiError;

export class AuthService {

    public static initRegistration(requestBody: InitRegistrationRequest): Promise<RegistrationResponse> {
        return HttpService.sendRequest<RegistrationSuccessResponse>(
            HttpMethod.POST,
            '/api/signup',
            JSON.stringify(requestBody),
            ContentType.JSON
        );
    }

    public static initSaasRegistration(requestBody: {
        email: string;
        source: string;
        newFlow: boolean;
        eventUrl?: string;
        sessionId?: string;
        referralCode?: string;
    }): Promise<{
        message: string;
        redirectUrl: string;
        userId: string;
        email: string;
        temporaryPassword: string;
    }> {
        return HttpService.sendRequest<{
            message: string;
            redirectUrl: string;
            userId: string;
            email: string;
            temporaryPassword: string;
        }>(
            HttpMethod.POST,
            '/api/signup-saas',
            JSON.stringify(requestBody),
            ContentType.JSON
        );
    }

    public static sendEmailVerificationCode(userId: string): Promise<{
        message: string
    }> {
        return HttpService.sendRequest<{ message: string }>(
            HttpMethod.POST,
            '/api/saas/user/send-verification-code',
            JSON.stringify({userId}),
            ContentType.JSON
        );
    }

    public static verifyEmailWithCode(requestBody: { code: string, userId: string, password: string }): Promise<{
        message: string
    }> {
        return HttpService.sendRequest<{ message: string }>(
            HttpMethod.POST,
            '/api/saas/user/verify-email',
            JSON.stringify(requestBody),
            ContentType.JSON
        );
    }

    public static fetchUserDataForRegistration(uuid: string): Promise<UserToActivate> {
        return HttpService.sendRequest<UserToActivate>(
            HttpMethod.GET,
            `/api/activate/${uuid}`,
            null,
            ContentType.JSON
        );
    }

    public static fetchPublicUserData(userId: string): Promise<PublicUser> {
        return HttpService.sendRequest<PublicUser>(
            HttpMethod.GET,
            `/api/saas/user/public/${userId}`,
        );
    }

    public static async activateAccount(activateAccountRequest: ActivateAccount): Promise<SignUpOutput> {
        return signUp({
            username: activateAccountRequest.email.toLowerCase(),
            password: activateAccountRequest.password,
            options: {
                userAttributes: {
                    email: activateAccountRequest.email.toLowerCase(),
                    name: activateAccountRequest.name,
                    "custom:promoCode": activateAccountRequest.promoCode ?? '',
                    "custom:confirmationCode": activateAccountRequest.confirmationCode
                },
                autoSignIn: true
            }
        })
    }

    public static async autoSignIn(): Promise<SignInOutput> {
        return autoSignIn();
    }

    public static async login(loginRequest: LoginRequest): Promise<SignInOutput> {
        return signIn({
            username: loginRequest.email.toLowerCase(),
            password: loginRequest.password,
        });
    }


    public static async facebookLogin(): Promise<void> {
        await signInWithRedirect({provider: 'Facebook'})
    }

    public static resetPassword(email: string): Promise<void> {
        return HttpService.sendRequest<void>(
            HttpMethod.POST,
            '/api/reset-password',
            JSON.stringify({email: email}),
            ContentType.JSON
        );
    }

    public static changePassword(changePasswordRequest: ChangePasswordRequest): Promise<void> {
        return HttpService.sendRequest<void>(
            HttpMethod.POST,
            '/api/change-password',
            JSON.stringify(changePasswordRequest),
            ContentType.JSON
        );
    }

    public static async signOut(): Promise<void> {
        return signOut();
    }

    public static async refreshUserSession() {
        try {
            return await fetchAuthSession({forceRefresh: true});
        } catch (error) {
            console.error("Error refreshing token:", error);
        }
    };
}